/**
 * Kit loader: the manifest, the meshopt-compressed piece GLB and, on demand, the material maps
 * (KTX2 through Basis Universal, or WebP). Pieces are extracted once into plain geometries in each
 * asset's own frame (see pipeline/blender/lib/kit.py); materials are created per finish and cached.
 */
import * as THREE from 'three';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { GLTFLoader } from '../vendor/GLTFLoader.js';
import { configureMap, createKitMaterial, overlayVariant, type MaterialMaps } from './materials';
import type { KitLod, KitManifest } from './types';

export interface PiecePrim {
  geometry: THREE.BufferGeometry;
  /** Material slot name authored in Blender (slot.shadow, Steel, …). */
  slot: string;
}

export type DetailSegments = Record<'start' | 'mid' | 'end', PiecePrim[]>;

export interface KitLoadOptions {
  /** URL of the kit folder holding manifest.json (default `${BASE_URL}kit/`). */
  baseUrl?: string;
  renderer: THREE.WebGLRenderer;
  /** Byte counter hook (every kit file fetched). */
  onBytes?: (url: string, bytes: number) => void;
}

/** Slot names in the GLB → kit material ids. Wood slots are bound per part by the assembler. */
export const SLOT_MATERIAL: Record<string, string> = {
  Steel: 'steel', Brass: 'brass', Black: 'black', 'slot.shadow': 'slot.shadow', 'slot.dowel': 'slot.dowel',
};

function toFloat(g: THREE.BufferGeometry): THREE.BufferGeometry {
  // meshopt quantisation leaves normalised integer attributes; transforms need floats
  const out = new THREE.BufferGeometry();
  for (const [name, attr] of Object.entries(g.attributes)) {
    const a = attr as THREE.BufferAttribute;
    const arr = new Float32Array(a.count * a.itemSize);
    for (let i = 0; i < a.count; i++) for (let c = 0; c < a.itemSize; c++) arr[i * a.itemSize + c] = a.getComponent(i, c);
    out.setAttribute(name, new THREE.BufferAttribute(arr, a.itemSize));
  }
  const index = g.getIndex();
  if (index) out.setIndex(Array.from(index.array as ArrayLike<number>));
  return out;
}

/** Split a loaded kit scene into per-asset, per-LOD geometries in each asset's own frame. */
export function extractPieces(scene: THREE.Object3D): { details: Map<string, Record<KitLod, DetailSegments>>; hardware: Map<string, Record<KitLod, PiecePrim[]>> } {
  const details = new Map<string, Record<KitLod, DetailSegments>>();
  const hardware = new Map<string, Record<KitLod, PiecePrim[]>>();
  scene.updateMatrixWorld(true);
  const roots = new Map<string, THREE.Object3D>();
  scene.traverse((o) => {
    if (o.userData.assetId && o.userData.kind) roots.set(o.userData.assetId as string, o);
  });
  const inv = new THREE.Matrix4();
  const rel = new THREE.Matrix4();
  scene.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    let tagged: THREE.Object3D | null = mesh;
    while (tagged && !tagged.userData.segment) tagged = tagged.parent;
    if (!tagged) return;
    const { assetId, lod, segment } = tagged.userData as { assetId: string; lod: KitLod; segment: string };
    const root = roots.get(assetId);
    if (!root) return;
    inv.copy(root.matrixWorld).invert();
    rel.multiplyMatrices(inv, mesh.matrixWorld);
    const geometry = toFloat(mesh.geometry);
    geometry.applyMatrix4(rel);
    // wood materials use vertex colours (per-part tint); kit pieces are untinted
    if (!geometry.getAttribute('color')) geometry.setAttribute('color', new THREE.Float32BufferAttribute(new Float32Array(geometry.getAttribute('position').count * 3).fill(1), 3));
    const slot = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material).name;
    const prim: PiecePrim = { geometry, slot };
    if (segment === 'mesh') {
      const rec = hardware.get(assetId) ?? ({ live: [], inspect: [] } as Record<KitLod, PiecePrim[]>);
      rec[lod].push(prim);
      hardware.set(assetId, rec);
    } else {
      const empty = (): DetailSegments => ({ start: [], mid: [], end: [] });
      const rec = details.get(assetId) ?? ({ live: empty(), inspect: empty() } as Record<KitLod, DetailSegments>);
      rec[lod][segment as 'start' | 'mid' | 'end'].push(prim);
      details.set(assetId, rec);
    }
  });
  return { details, hardware };
}

/** What the assembler needs from a kit (the loaded `Kit`, or a test double). */
export interface KitLike {
  readonly manifest: KitManifest;
  readonly details: Map<string, Record<KitLod, DetailSegments>>;
  readonly hardware: Map<string, Record<KitLod, PiecePrim[]>>;
  material(id: string, finishId?: string, overlay?: boolean): THREE.Material;
}

export class Kit implements KitLike {
  readonly manifest: KitManifest;
  readonly baseUrl: string;
  readonly details = new Map<string, Record<KitLod, DetailSegments>>();
  readonly hardware = new Map<string, Record<KitLod, PiecePrim[]>>();
  /** Bytes fetched so far (manifest, GLB, transcoder, maps). */
  bytesLoaded = 0;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly ktx: KTX2Loader | null;
  private readonly maps = new Map<string, Promise<MaterialMaps>>();
  /** One decode and one GPU texture per file; entries sharing a file get clones (own repeat). */
  private readonly files = new Map<string, Promise<THREE.Texture>>();
  private readonly loadedMaps = new Map<string, MaterialMaps>();
  private readonly materials = new Map<string, THREE.MeshPhysicalMaterial>();
  private readonly onBytes?: (url: string, bytes: number) => void;
  private disposed = false;

  private constructor(manifest: KitManifest, baseUrl: string, renderer: THREE.WebGLRenderer, ktx: KTX2Loader | null, onBytes?: (url: string, bytes: number) => void) {
    this.manifest = manifest;
    this.baseUrl = baseUrl;
    this.renderer = renderer;
    this.ktx = ktx;
    this.onBytes = onBytes;
  }

  static async load(opts: KitLoadOptions): Promise<Kit> {
    const base = opts.baseUrl ?? `${import.meta.env.BASE_URL}kit/`;
    const res = await fetch(`${base}manifest.json`);
    if (!res.ok) throw new Error(`Kit manifest ${res.status}`);
    const text = await res.text();
    const manifest = JSON.parse(text) as KitManifest;
    if (manifest.schemaVersion !== 1) throw new Error(`Unsupported kit manifest ${manifest.schemaVersion}`);
    let ktx: KTX2Loader | null = null;
    if (manifest.textureFormat === 'ktx2' && manifest.transcoder) {
      ktx = new KTX2Loader().setTranscoderPath(new URL(manifest.transcoder.path, new URL(base, location.href)).href).setWorkerLimit(2).detectSupport(opts.renderer);
    }
    const kit = new Kit(manifest, base, opts.renderer, ktx, opts.onBytes);
    kit.count(`${base}manifest.json`, text.length);
    if (ktx && manifest.transcoder) for (const f of manifest.transcoder.files) kit.count(base + f.file, f.bytes);
    await kit.loadPieces();
    return kit;
  }

  private count(url: string, bytes: number) {
    this.bytesLoaded += bytes;
    this.onBytes?.(url, bytes);
  }

  private async loadPieces() {
    const url = this.baseUrl + this.manifest.kit.file;
    const buf = await (await fetch(url)).arrayBuffer();
    this.count(url, buf.byteLength);
    const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
    const gltf = await loader.parseAsync(buf, this.baseUrl);
    const scene = gltf.scene;
    const pieces = extractPieces(scene);
    for (const [k, v] of pieces.details) this.details.set(k, v);
    for (const [k, v] of pieces.hardware) this.hardware.set(k, v);
    scene.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.isMesh) mesh.geometry.dispose();
    });
  }

  /** Load the maps of these kit materials (idempotent). */
  async ensureMaterials(ids: Iterable<string>): Promise<void> {
    await Promise.all([...new Set(ids)].map((id) => this.mapsFor(id)));
  }

  private mapsFor(id: string): Promise<MaterialMaps> {
    const entry = this.manifest.materials[id];
    if (!entry) return Promise.reject(new Error(`Kit has no material ${id}`));
    let p = this.maps.get(id);
    if (!p) {
      p = (async () => {
        const out: MaterialMaps = {};
        const aniso = this.renderer.capabilities.getMaxAnisotropy();
        await Promise.all(Object.entries(entry.files ?? {}).map(async ([kind, f]) => {
          const url = this.baseUrl + f.file;
          let base = this.files.get(url);
          if (!base) {
            base = this.loadTexture(url, kind === 'colour');
            this.files.set(url, base);
            base.then(() => this.count(url, f.bytes), () => undefined);
          }
          const t = (await base).clone();
          configureMap(t, entry, aniso);
          out[kind as keyof MaterialMaps] = t;
        }));
        if (this.disposed) for (const t of Object.values(out)) t.dispose();
        this.loadedMaps.set(id, out);
        return out;
      })();
      this.maps.set(id, p);
    }
    return p;
  }

  private loadTexture(url: string, colour: boolean): Promise<THREE.Texture> {
    if (this.ktx) return this.ktx.loadAsync(url);
    return new THREE.TextureLoader().loadAsync(url).then((t) => {
      if (colour) t.colorSpace = THREE.SRGBColorSpace;
      return t;
    });
  }

  /** The material for a kit material id and finish. Its maps must have been ensured first. */
  material(id: string, finishId: string = this.manifest.defaultFinish, overlay = false): THREE.MeshPhysicalMaterial {
    const key = `${id}|${finishId}|${overlay ? 'o' : ''}`;
    let m = this.materials.get(key);
    if (!m) {
      const entry = this.manifest.materials[id];
      if (!entry) throw new Error(`Kit has no material ${id}`);
      const finish = this.manifest.finishes[finishId] ?? this.manifest.finishes[this.manifest.defaultFinish];
      const maps = this.loadedMaps.get(id);
      if (entry.files && !maps) throw new Error(`Kit material ${id} not loaded (call ensureMaterials first)`);
      const base = createKitMaterial(id, entry, maps ?? {}, finish, finishId);
      m = overlay ? overlayVariant(base) : base;
      if (overlay) base.dispose();
      this.materials.set(key, m);
    }
    return m;
  }

  /** True when the material's maps are loaded (or it has none), so `material()` can be called. */
  hasMaterial(id: string): boolean {
    const entry = this.manifest.materials[id];
    return !!entry && (!entry.files || this.loadedMaps.has(id));
  }

  /** Every map texture (for a context restore). */
  listTextures(): THREE.Texture[] {
    return [...this.loadedMaps.values()].flatMap((m) => Object.values(m).filter((t): t is THREE.Texture => !!t));
  }

  /** Every material created so far (for highlight variants, theme passes). */
  listMaterials(): THREE.MeshPhysicalMaterial[] {
    return [...this.materials.values()];
  }

  dispose(): void {
    this.disposed = true;
    for (const m of this.materials.values()) m.dispose();
    for (const maps of this.loadedMaps.values()) for (const t of Object.values(maps)) t?.dispose();
    for (const p of this.files.values()) p.then((t) => t.dispose(), () => undefined);
    for (const rec of this.details.values()) for (const lod of Object.values(rec)) for (const seg of Object.values(lod)) for (const p of seg) p.geometry.dispose();
    for (const rec of this.hardware.values()) for (const lod of Object.values(rec)) for (const p of lod) p.geometry.dispose();
    this.ktx?.dispose();
    this.materials.clear();
  }
}
